import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

/**
 * Search + filters card used above every list table.
 * Pages keep their own search trigger: `searchSubmit` fires on Enter (and the
 * optional go button), `searchChange` on every keystroke for two-way binding.
 * Filters are projected into the pills row; tools go in `[toolbarEnd]`.
 */
@Component({
  selector: 'app-list-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pp-toolbar" role="search">
      <div class="pp-toolbar__row">
        <label *ngIf="showSearch" class="pp-toolbar__search">
          <i class="pi pi-search" aria-hidden="true"></i>
          <input
            type="text"
            [ngModel]="searchValue"
            (ngModelChange)="onInput($event)"
            (keydown.enter)="searchSubmit.emit(searchValue)"
            (keydown.escape)="searchValue && onClear()"
            [placeholder]="placeholder"
            [attr.aria-label]="placeholder" />
          <button
            *ngIf="searchValue"
            type="button"
            class="pp-toolbar__icon-btn"
            (click)="onClear()"
            aria-label="Clear search">
            <i class="pi pi-times"></i>
          </button>
          <button
            *ngIf="searchButton"
            type="button"
            class="pp-toolbar__icon-btn pp-toolbar__icon-btn--go"
            (click)="searchSubmit.emit(searchValue)"
            aria-label="Search">
            <i class="pi pi-arrow-right"></i>
          </button>
        </label>
        <div class="pp-toolbar__pills pp-toolbar__pills--inline" *ngIf="!showSearch">
          <ng-container *ngTemplateOutlet="pillsTpl"></ng-container>
        </div>
        <div class="pp-toolbar__end"><ng-content select="[toolbarEnd]"></ng-content></div>
      </div>
      <div class="pp-toolbar__pills" *ngIf="showSearch && pills">
        <ng-container *ngTemplateOutlet="pillsTpl"></ng-container>
      </div>
    </div>
    <ng-template #pillsTpl>
      <span class="pp-toolbar__pills-label"><i class="pi pi-sliders-h"></i> Filters</span>
      <ng-content></ng-content>
      <button *ngIf="showClearAll" type="button" class="pp-toolbar__clear-all" (click)="onClearAll()">
        Clear all
      </button>
    </ng-template>
  `,
  styles: [
    ':host { display: block; }',
    '.pp-toolbar__pills--inline { flex: 1 1 auto; min-width: 0; }',
  ],
})
export class ListToolbarComponent implements OnChanges {
  @Input() search = '';
  @Input() placeholder = 'Search…';
  @Input() showSearch = true;
  /** Show an explicit "go" button inside the search box (pages that search on demand). */
  @Input() searchButton = false;
  /** Render the filters row (set false when the page has no filters). */
  @Input() pills = true;
  @Input() showClearAll = false;

  /** Local mirror so parent-only clears (empty state / reset) update the input under OnPush. */
  searchValue = '';

  @Output() searchChange = new EventEmitter<string>();
  @Output() searchSubmit = new EventEmitter<string>();
  @Output() searchClear = new EventEmitter<void>();
  @Output() clearAll = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['search']) {
      this.searchValue = this.search ?? '';
    }
  }

  onInput(value: string): void {
    this.searchValue = value;
    this.search = value;
    this.searchChange.emit(value);
  }

  onClear(): void {
    this.searchValue = '';
    this.search = '';
    this.searchChange.emit('');
    this.searchClear.emit();
  }

  /** Clear-all must also wipe the search box (OnPush + parent-only clear left stale text). */
  onClearAll(): void {
    if (this.searchValue || this.search) {
      this.searchValue = '';
      this.search = '';
      this.searchChange.emit('');
    }
    this.clearAll.emit();
  }
}
